import React, {useCallback, useEffect, useMemo, useState} from 'react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from 'date-fns';
import Grid from "@mui/material/Grid2";
import {connect} from "react-redux";
import Loader from "./layout/loading/loader";
import {Link, useParams} from "react-router-dom";
import {patrimoinesActions} from "../../modules/patrimoines/patrimoines.actions";
import GraffitiMap from "./graffiti/GraffitiMap";
import {FolderDelete} from "@mui/icons-material";
import PageTitle from "./layout/PageTitle";
import Button from "@mui/material/Button";
import MapIcon from '@mui/icons-material/Map';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import Constants from "../../utils/constants";
import GraffitiPatrimoineInterventionsList from "./graffiti/GraffitiPatrimoineInterventionsList.js";
import {graffitiActions} from "../../modules/graffiti/graffiti.actions.js";
import { useLazySearchRuesQuery, useGetInterventionsQuery, useExportGraffitiMutation } from "../../modules/graffiti/graffiti.api.js";
import GraffitiInterventionsFilters from "./graffiti/GraffitiInterventionsFilters";


const getMode = () => {
    const storedMode = window.localStorage.getItem("graffiti_patrimoine_individual_view_mode");
    return storedMode || Constants.MODE_LIST;
};

function GraffitiPatrimoineIndividualDetailPage({dispatch, ...props}) {
    const params = useParams();
    const patrimoineId = params?.id;
    const [currentConteneurs, setCurrentConteneurs] = useState(null)
    const [mode, setMode] = useState(() => getMode())
    // Nouveau système de période: preset + bornes de dates
    const [preset, setPreset] = useState('this_week');
    const [dateDebut, setDateDebut] = useState(null);
    const [dateFin, setDateFin] = useState(null);
    const [selectedCommunes, setSelectedCommunes] = useState([]);
    const [selectedQuartiers, setSelectedQuartiers] = useState([]);
    const [selectedRues, setSelectedRues] = useState([]);
    const [exporting, setExporting] = useState(false);

    // Resolve clientId once and reuse it across effects/callbacks
    const clientId = useMemo(() => (props.client?.id ?? null), [props.client?.id]);

    // Rues lazy search
    const [triggerSearchRues, { data: ruesOptions = [], isFetching: ruesLoading }] = useLazySearchRuesQuery();
    const onRuesInputChange = useCallback((value) => {
        if (!clientId) return;
        if (value && value.length >= 2) {
            triggerSearchRues({ clientId, q: value });
        }
    }, [clientId, triggerSearchRues]);


    useEffect(() => {
        if (patrimoineId && (props.patrimoine?.id !== parseInt(patrimoineId)) && !props.loading && !props.error) {
            dispatch(patrimoinesActions.fetchPatrimoine(patrimoineId));
        }
    }, [dispatch, patrimoineId, props.patrimoine, props.loading, props.error]);

    // Load metadata for filters (communes/quartiers)
    useEffect(() => {
        if (clientId) {
            dispatch(graffitiActions.fetchMetadata(clientId));
        }
    }, [dispatch, clientId]);

    const handleModeChange = (value) => {
        window.localStorage.setItem("graffiti_patrimoine_individual_view_mode", value);
        setMode(value)
    }

    // Helper: format date to YYYY-MM-DD
    const fmt = (d) => format(d, 'yyyy-MM-dd');
    // Compute dates from preset
    const computeDatesForPreset = useCallback((p) => {
        const today = new Date();
        switch (p) {
            case 'this_week': {
                const d1 = startOfWeek(today, { weekStartsOn: 1 });
                const d2 = endOfWeek(today, { weekStartsOn: 1 });
                return { d1: fmt(d1), d2: fmt(d2) };
            }
            case 'last_week': {
                const oneWeekAgo = subWeeks(today, 1);
                const d1 = startOfWeek(oneWeekAgo, { weekStartsOn: 1 });
                const d2 = endOfWeek(oneWeekAgo, { weekStartsOn: 1 });
                return { d1: fmt(d1), d2: fmt(d2) };
            }
            case 'this_month': {
                const d1 = startOfMonth(today);
                const d2 = endOfMonth(today);
                return { d1: fmt(d1), d2: fmt(d2) };
            }
            case 'last_month': {
                const lastM = subMonths(today, 1);
                const d1 = startOfMonth(lastM);
                const d2 = endOfMonth(lastM);
                return { d1: fmt(d1), d2: fmt(d2) };
            }
            default:
                return { d1: null, d2: null };
        }
    }, []);

    // Initialize and update dates when preset changes
    useEffect(() => {
        if (!preset) return; // manual dates
        const { d1, d2 } = computeDatesForPreset(preset);
        setDateDebut(d1);
        setDateFin(d2);
    }, [preset, computeDatesForPreset]);

    // Options from metadata
    const communes = props.graffiti?.metadata?.communes || props.graffiti?.communes || [];
    const quartiers = props.graffiti?.metadata?.quartiers || props.graffiti?.quartiers || [];

    function getOptionId(opt) {
        if (!opt) return null;
        return (typeof opt.id !== 'undefined') ? opt.id : (typeof opt.value !== 'undefined' ? opt.value : null);
    }
    function getOptionLabel(opt) {
        if (!opt) return '';
        return opt.libelle || opt.label || opt.nom || String(opt.id ?? opt.value ?? '');
    }
    function normalizeIdArray(values) {
        if (!Array.isArray(values)) return [];
        return values.map(v => getOptionId(v)).filter(v => Number.isFinite(Number(v))).map(v => Number(v));
    }

    const queryArgs = useMemo(() => ({
        patrimoineId: patrimoineId,
        dateDebut: dateDebut,
        dateFin: dateFin,
        communeIds: normalizeIdArray(selectedCommunes),
        quartierIds: normalizeIdArray(selectedQuartiers),
        rueIds: normalizeIdArray(selectedRues),
    }), [patrimoineId, dateDebut, dateFin, selectedCommunes, selectedQuartiers, selectedRues]);

    const { data: interventions = [], isFetching, isLoading, error } = useGetInterventionsQuery(queryArgs, { skip: !patrimoineId || !dateDebut || !dateFin });

    const [exportGraffiti] = useExportGraffitiMutation();

    const handleExport = useCallback(async () => {
        if (!patrimoineId || !dateDebut || !dateFin) return;
        try {
            setExporting(true);
            const result = await exportGraffiti({ ...queryArgs }).unwrap();
            // If server returned JSON (debug mode), open in new tab as text
            if (result && !(result instanceof Blob) && typeof result === 'object') {
                const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(result, null, 2));
                const a = document.createElement('a');
                a.href = dataStr;
                a.download = `export_graffiti_${patrimoineId}_${dateDebut}_${dateFin}.json`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                return;
            }
            const blob = result instanceof Blob ? result : new Blob([result]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `export_graffiti_${patrimoineId}_${dateDebut}_${dateFin}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Export error', e);
        } finally {
            setExporting(false);
        }
    }, [exportGraffiti, patrimoineId, dateDebut, dateFin, queryArgs]);

    // Important: keep hooks order stable. Perform loading check AFTER all hooks are declared
    if (props.loading || !props.patrimoine) {
        return <Loader/>;
    }


    return (
        <>
            <PageTitle title={`Patrimoine ${props.patrimoine?.libelle}`} icon={<FolderDelete/>}/>
            <Grid container justifyContent={"center"} spacing={2} className={"mb-2"}>
                <Grid item>
                    <Button
                        variant={mode === Constants.MODE_LIST ? "contained" : "outlined"}
                        color="primary"
                        startIcon={<FormatListNumberedIcon/>}
                        onClick={() => handleModeChange(Constants.MODE_LIST)}
                    >
                        Affichage liste
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant={mode === Constants.MODE_MAP ? "contained" : "outlined"}
                        color="primary"
                        startIcon={<MapIcon/>}
                        onClick={() => handleModeChange(Constants.MODE_MAP)}
                    >
                        Affichage carte
                    </Button>
                </Grid>
            </Grid>
            <Grid container>
                {/* Filtres (période + zones) */}
                <Grid item size={{ xs: 12 }}>
                    <GraffitiInterventionsFilters
                        preset={preset}
                        onPresetChange={(p) => setPreset(p)}
                        dateDebut={dateDebut}
                        dateFin={dateFin}
                        onDateDebutChange={(val) => { setPreset(null); setDateDebut(val); }}
                        onDateFinChange={(val) => { setPreset(null); setDateFin(val); }}
                        communes={communes}
                        quartiers={quartiers}
                        ruesOptions={ruesOptions}
                        ruesLoading={ruesLoading}
                        selectedCommunes={selectedCommunes}
                        selectedQuartiers={selectedQuartiers}
                        selectedRues={selectedRues}
                        onCommunesChange={setSelectedCommunes}
                        onQuartiersChange={setSelectedQuartiers}
                        onRuesChange={setSelectedRues}
                        onRuesInputChange={onRuesInputChange}
                        onExportClick={handleExport}
                        exportLoading={exporting}
                    />
                </Grid>

                {mode === Constants.MODE_MAP && (
                    <Grid item size={{ xs: 12 }}>
                        <GraffitiMap
                            selectedCommunes={selectedCommunes}
                            selectedQuartiers={selectedQuartiers}
                            selectedRues={selectedRues}
                            interventions={interventions}
                        />
                    </Grid>
                )}

                {mode === Constants.MODE_LIST && (
                    <Grid item size={{ xs: 12 }}>
                        <GraffitiPatrimoineInterventionsList
                            rows={interventions}
                            loading={isLoading || isFetching}
                            error={error}
                        />
                    </Grid>
                )}
            </Grid>

        </>
    )
}

const mapStateToProps = function (state) {
    return {
        patrimoine: state.patrimoines.patrimoine,
        loading: state.patrimoines.loading,
        error: state.patrimoines.error,
        client: state.users.user,
        graffiti: state.graffiti,
    }
}

export default connect(mapStateToProps)(GraffitiPatrimoineIndividualDetailPage);